import { db } from '../firebase';
import { collection, addDoc, getDocs, getDoc, doc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';

// Funciones genéricas para interactuar con Firestore

/**
 * Agrega un nuevo documento a una colección.
 * @param {string} collectionName - El nombre de la colección.
 * @param {object} data - Los datos del documento a agregar.
 * @returns {Promise<string>} - El ID del nuevo documento.
 */
/**
 * Agrega un nuevo documento a una colección con manejo de errores mejorado.
 * @param {string} collectionName - El nombre de la colección.
 * @param {object} data - Los datos del documento a agregar.
 * @returns {Promise<string>} - El ID del nuevo documento.
 * @throws {Error} - Si hay un error al agregar el documento.
 */
export const addDocument = async (collectionName, data) => {
  try {
    const docRef = await addDoc(collection(db, collectionName), data);
    return docRef.id;
  } catch (error) {
    console.error(`Error al agregar documento a la colección ${collectionName}:`, error);
    throw new Error(`Error al guardar los datos en ${collectionName}. Por favor, verifica tu conexión e inténtalo de nuevo.`);
  }
};

/**
 * Obtiene todos los documentos de una colección.
 * @param {string} collectionName - El nombre de la colección.
 * @returns {Promise<Array<object>>} - Un array de documentos.
 */
/**
 * Obtiene todos los documentos de una colección con manejo de errores mejorado.
 * @param {string} collectionName - El nombre de la colección.
 * @returns {Promise<Array<object>>} - Un array de documentos.
 * @throws {Error} - Si hay un error al acceder a la colección.
 */
export const getCollection = async (collectionName) => {
  try {
    const querySnapshot = await getDocs(collection(db, collectionName));
    if (querySnapshot.empty) {
      console.log(`La colección ${collectionName} está vacía.`);
      return [];
    }
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error(`Error al obtener documentos de la colección ${collectionName}:`, error);
    throw new Error(`Error al cargar datos de ${collectionName}. Por favor, verifica tu conexión e inicia sesión nuevamente.`);
  }
};

/**
 * Obtiene un documento específico por su ID.
 * @param {string} collectionName - El nombre de la colección.
 * @param {string} id - El ID del documento.
 * @returns {Promise<object|null>} - El documento o null si no existe.
 */
/**
 * Obtiene un documento específico por su ID con manejo de errores mejorado.
 * @param {string} collectionName - El nombre de la colección.
 * @param {string} id - El ID del documento.
 * @returns {Promise<object|null>} - El documento o null si no existe.
 * @throws {Error} - Si hay un error al acceder al documento.
 */
export const getDocument = async (collectionName, id) => {
  try {
    const docRef = doc(db, collectionName, id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      console.log(`No se encontró el documento con ID ${id} en la colección ${collectionName}`);
      return null;
    }
    
    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    console.error(`Error al obtener el documento ${id} de la colección ${collectionName}:`, error);
    throw new Error(`Error al cargar el documento de ${collectionName}. Por favor, verifica tu conexión e inicia sesión nuevamente.`);
  }
};

/**
 * Actualiza un documento existente.
 * @param {string} collectionName - El nombre de la colección.
 * @param {string} id - El ID del documento a actualizar.
 * @param {object} data - Los datos a actualizar.
 */
/**
 * Actualiza un documento existente con manejo de errores mejorado.
 * @param {string} collectionName - El nombre de la colección.
 * @param {string} id - El ID del documento a actualizar.
 * @param {object} data - Los datos a actualizar.
 * @throws {Error} - Si hay un error al actualizar el documento.
 */
export const updateDocument = async (collectionName, id, data) => {
  try {
    const docRef = doc(db, collectionName, id);
    await updateDoc(docRef, data);
  } catch (error) {
    console.error(`Error al actualizar el documento ${id} en la colección ${collectionName}:`, error);
    throw new Error(`Error al guardar los cambios en ${collectionName}. Por favor, verifica tu conexión e inténtalo de nuevo.`);
  }
};

/**
 * Elimina un documento.
 * @param {string} collectionName - El nombre de la colección.
 * @param {string} id - El ID del documento a eliminar.
 */
export const deleteDocument = (collectionName, id) => {
  const docRef = doc(db, collectionName, id);
  return deleteDoc(docRef);
};

/**
 * Busca documentos en una colección por un campo y valor exacto.
 * @param {string} collectionName - El nombre de la colección.
 * @param {string} fieldName - El nombre del campo a comparar.
 * @param {any} value - El valor a buscar.
 * @returns {Promise<Array<object>>} - Un array de documentos que coinciden.
 */
export const findDocumentsByField = async (collectionName, fieldName, value) => {
  const q = query(collection(db, collectionName), where(fieldName, '==', value));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
};
